<script>
	import { onDestroy, onMount } from "svelte";
	import { Route, navigateTo } from "svelte-router-spa";
	import { PRConnect } from "@projectroom/ui-lib";
	import { planStore, isCreepong } from "./store/plan-store";
	import { messaging } from "./store/messaging-store";
	import { ThemeToggle, Dialog, Button, Input } from "./../lib/ui";
	import AccountMenu from "./common/AccountMenu.svelte";

	export let currentRoute;
	export let params;

	let title = "";
	let botId;
	let pageUrl;
	let qrUrl;
	let qrLoading = false;
	let messagingSubsc;

	// 게시 다이얼로그. 다른 빌더와 같은 규약으로 **레이아웃이 소유**한다 —
	// 버튼은 에디터 sticky 툴바(GameBuilder.svelte)에 있고, 열기 요청만 messaging 으로 올라온다.
	let publishOpen = false;
	let posting = false;
	let publishError = "";
	let publishedJustNow = false;
	let publishedTimer;

	// 상단바는 ChatBuilderLayout 과 같은 자리·같은 높이(56px)다. GameBuilder 의 .builder-shell 이
	// 그만큼 내려와 앉는다 — 높이를 바꾸면 양쪽을 함께 고쳐야 한다.
	onMount(async () => {
		// ⚠️ Svelte 스토어는 **구독 즉시 현재 값으로 한 번** 콜백을 부른다. messaging 은 앱 전역
		//    싱글턴이라 그 값이 지난 방문에서 남긴 openPublish 일 수 있다 → 첫 호출은 버린다.
		//    안 버리면 빌더에 들어오자마자 팝업이 뜨거나, 심지어 첫 게시가 저절로 나간다.
		let replayed = false;
		messagingSubsc = messaging.subscribe((message) => {
			if (!replayed) {
				replayed = true;
				return;
			}
			if (message?.target !== "GameBuilderLayout") return;
			if (message.action === "openPublish") openPublish(message);
		});
		await planStore.init();
		botId = currentRoute.namedParams.botId;
		PRConnect.send("/api/Bot/getBotInfo", { id: botId }, (data) => {
			if (data.header.status === "success") {
				const botData = data.result?.botData || {};
				title = botData.title || "";
				pageUrl = botData.pageUrl;
				// DB 의 QR_URL 은 네이버 단축URL 기반이라 로컬에선 비어 있거나 깨진다
				// (undefined.qr — 봇 타입 공통). 화면에 뜨는 QR 은 주소로 그 자리에서 만들고,
				// 저장된 단축URL QR 은 [불러오기]로 가져온다.
				qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(botData.pageUrl || "")}&size=100x100`;
			}
		});
	});

	onDestroy(() => {
		messagingSubsc && messagingSubsc();
		clearTimeout(publishedTimer);
	});

	/**
	 * 한 번도 게시 안 한 게임은 **먼저 게시하고** 팝업을 연다(ChatBuilderLayout 과 같은 규약).
	 * 게시 전 주소를 보여줘 봐야 그 주소엔 아직 아무것도 없다.
	 * 이미 게시된 게임은 팝업만 열고, 반영은 [변경사항 반영]에서 한다.
	 */
	const openPublish = ({ isPosted = true } = {}) => {
		publishError = "";
		// 크리퐁은 팝업 없이 반영만 한다(상단바도 숨기는 임베드 모드).
		if ($isCreepong || !isPosted) {
			doPosting();
			return;
		}
		publishOpen = true;
	};

	const failPosting = (data) => {
		posting = false;
		// 조용히 실패하면 저작자는 게시된 줄 안다 → 반드시 화면에 남긴다(챗 빌더가 겪었던 함정).
		publishError = data?.header?.message
			? `게시에 실패했어요. ${data.header.message}`
			: "게시에 실패했어요. 잠시 후 다시 시도해주세요.";
		if (!$isCreepong) publishOpen = true;
	};

	// 실제 게시. postChangeBotData 가 BOT_GAME_SETTINGS_C 스냅샷 + 게시본 셸 재굽기까지 한다.
	// ⚠️ in-flight 가드: 연타하면 같은 게시가 두 번 나간다.
	const doPosting = () => {
		if (posting) return;
		posting = true;
		publishError = "";
		PRConnect.send(
			"/api/Post/postChangeBotData",
			{ botId },
			(data) => {
				posting = false;
				if (data.header.status !== "success") {
					failPosting(data);
					return;
				}
				// sticky 툴바의 '미반영 변경' 점을 즉시 끈다(다음 저장/새로고침을 기다리지 않는다).
				messaging.set({ target: "GameBuilder", action: "published" });
				if ($isCreepong) return;
				publishOpen = true;
				publishedJustNow = true;
				clearTimeout(publishedTimer);
				publishedTimer = setTimeout(() => (publishedJustNow = false), 4000);
			},
			failPosting,
		);
	};

	const clickCopy = () => {
		if (!pageUrl) return;
		navigator.clipboard.writeText(pageUrl);
	};

	// 저장된 단축URL QR 을 새로 발급받아 온다(네이버 shorturl). 로컬에선 미연결이라 실패할 수 있다.
	const clickRefreshQr = () => {
		qrLoading = true;
		PRConnect.send("/api/Bot/updateBotQrUrl", { botId }, (data) => {
			qrLoading = false;
			if (data.header.status === "success") qrUrl = data.result;
		});
	};
</script>

<div class="lite-wrap">
	{#if !$isCreepong}
		<header
			class="builder-topbar tw-fixed tw-inset-x-0 tw-top-0 tw-z-50 tw-flex tw-h-14 tw-items-center tw-justify-between tw-border-b tw-border-border tw-bg-sidebar tw-font-sans"
			style="padding-inline: max(1rem, calc((100vw - var(--app-max-w, 1920px)) / 2))"
		>
			<div class="tw-flex tw-items-center tw-gap-2.5">
				<button
					type="button"
					class="tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-transition-colors hover:tw-bg-muted"
					on:click={() => navigateTo("/main")}
					aria-label="아바톡 홈"
					title="아바톡 홈"
				>
					<img class="tw-h-6 tw-w-6" src="/static/images/avatalk-charecter-pink.svg" alt="아바톡" />
				</button>
				<div class="tw-h-4 tw-w-px tw-bg-border" />
				<div class="tw-flex tw-items-center tw-gap-1.5 tw-text-sm">
					<span class="tw-font-semibold tw-text-foreground">{title || "게임"}</span>
					<span class="tw-text-muted-foreground">/ 게임 빌더</span>
				</div>
			</div>
			<div class="tw-flex tw-items-center tw-gap-2">
				<!-- 저장 상태는 콘텐츠 헤더의 인디케이터가 담당(상단바 중복 표기 제거). -->
				<ThemeToggle />
				<div class="tw-h-4 tw-w-px tw-bg-border" />
				<AccountMenu showEmail={false} />
			</div>
		</header>
	{/if}

	<Route {currentRoute} {params} />

	<!-- 게시 설정 팝업. <Route/> **뒤에** 두는 이유는 게임 빌더의 프리뷰 드로어가 같은
	     z-index(1000)라, 앞에 두면 좁은 화면에서 드로어가 팝업 위를 덮기 때문이다
	     (같은 층은 뒤에 온 쪽이 이긴다 — ChatBuilderLayout 과 같은 이유). -->
	{#if !$isCreepong}
		<Dialog open={publishOpen} title="게시하기" on:close={() => (publishOpen = false)}>
			<div class="tw-space-y-4 tw-font-sans">
				{#if publishError}
					<div
						class="tw-rounded-md tw-border tw-border-destructive/30 tw-bg-destructive/10 tw-px-3 tw-py-2.5 tw-text-sm tw-text-destructive"
						role="alert"
					>
						{publishError}
					</div>
				{:else if publishedJustNow}
					<div
						class="tw-rounded-md tw-border tw-border-primary/30 tw-bg-primary/10 tw-px-3 tw-py-2.5 tw-text-sm tw-text-foreground"
						role="status"
					>
						게시했어요! 아래 주소로 바로 확인할 수 있어요.
					</div>
				{/if}

				<section class="tw-space-y-2.5">
					<div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
						<h3 class="tw-text-sm tw-font-semibold tw-text-foreground">게임 페이지 URL</h3>
						<Button variant="ghost" size="sm" on:click={() => window.open(pageUrl, "_blank")} disabled={!pageUrl}>
							게임 열기
						</Button>
					</div>
					<div class="tw-flex tw-items-center tw-gap-2">
						<Input type="text" class="tw-flex-1" value={pageUrl ?? ""} readonly aria-label="게임 페이지 URL" />
						<Button variant="outline" size="sm" on:click={clickCopy} disabled={!pageUrl}>복사</Button>
					</div>
					{#if qrUrl}
						<div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-pt-1">
							<h3 class="tw-text-sm tw-font-semibold tw-text-foreground">게임 페이지 QR</h3>
							<Button variant="ghost" size="sm" on:click={clickRefreshQr} disabled={qrLoading}>
								{qrLoading ? "불러오는 중…" : "불러오기"}
							</Button>
						</div>
						<div class="tw-flex tw-justify-center tw-rounded-lg tw-border tw-border-border tw-bg-muted/40 tw-p-3">
							<!-- QR 은 어두운 배경에서도 읽혀야 하므로 흰 판 위에 둔다 -->
							<img class="tw-rounded tw-bg-white tw-p-1.5" src={qrUrl} width="100" height="100" alt="게임 페이지 QR 코드" />
						</div>
					{/if}
				</section>

				<p class="tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
					게시하면 지금 저장된 저작 내용이 공개본에 반영됩니다. 게시 전까지 공개된 게임은 그대로예요.
				</p>
			</div>

			<svelte:fragment slot="footer">
				<Button variant="outline" on:click={() => (publishOpen = false)}>닫기</Button>
				<Button on:click={doPosting} disabled={posting}>
					{posting ? "반영 중…" : "변경사항 반영"}
				</Button>
			</svelte:fragment>
		</Dialog>
	{/if}
</div>
